export interface Language {
  code: string;
  display: string;
  native: string;
}

/**
 * Gets a single language by BCP 47 language tag (supports regional codes like en-CA, fr-CA)
 * Uses the native Intl.DisplayNames API - no external dependencies required!
 */
export function getLanguage(code: string, displayLocale: string = "en"): Language | null {
  try {
    if (!isValidLanguageCode(code)) {
      return null;
    }

    // Normalize the code to a canonical form
    const [canonicalCode] = Intl.getCanonicalLocales([code]);

    const displayNames = new Intl.DisplayNames([displayLocale], {
      type: "language",
      fallback: "code",
    });

    const nativeDisplayNames = new Intl.DisplayNames([canonicalCode], {
      type: "language",
      fallback: "code",
    });

    const display = displayNames.of(canonicalCode) || canonicalCode;
    const native = nativeDisplayNames.of(canonicalCode) || display;

    return {
      code: canonicalCode,
      display,
      native,
    };
  } catch (error) {
    console.error(`Failed to get language info for ${code}:`, error);
    return null;
  }
}

/**
 * Gets languages for specific BCP 47 codes
 */
export function getLanguages(codes: string[], displayLocale: string = "en"): Language[] {
  try {
    const languages: Language[] = [];

    for (const code of codes) {
      const language = getLanguage(code, displayLocale);
      if (language) {
        languages.push(language);
      }
    }

    return languages;
  } catch (error) {
    console.error("Failed to get languages for codes:", codes, error);
    return [];
  }
}

/**
 * Validates if a language code is a valid BCP 47 language tag
 * Supports both simple codes (en, fr) and regional codes (en-CA, fr-CA)
 */
export function isValidLanguageCode(code: string): boolean {
  try {
    if (!code || typeof code !== "string") {
      return false;
    }

    // Normalize the code to a canonical form
    const [canonicalCode] = Intl.getCanonicalLocales([code]);

    const displayNames = new Intl.DisplayNames(["en"], {
      type: "language",
      fallback: "code",
    });

    const displayName = displayNames.of(canonicalCode);

    // For regional codes (containing hyphen), accept if getCanonicalLocales passed
    if (canonicalCode.includes("-")) {
      return true;
    }

    // If displayName equals the code, it means it's not a recognized language
    // Valid language codes should return actual language names (e.g., "en" → "English")
    if (displayName === canonicalCode) {
      return false;
    }

    return true;
  } catch (error) {
    console.error("Failed to validate language code:", code, error);
    return false;
  }
}
